<script setup lang="ts">
import { useRoute } from 'vue-router';
import axios from 'axios';
import { Toaster } from 'vue-sonner';

const appHash = ref<string>('');
const frequency = ref<number>(2 * 60 * 1000);
const exceptionErrors = ref<string[]>([]);

const errorsDialogId = 'app-errors-modal';
const route = useRoute();
const { $events } = useEvents();
const { $alert } = useAlerts();
const { $log } = useLogging();
const { $t } = useLocalization();

onMounted(() =>
{
    $events.$on('errors-modal', toggleErrorsModal);
});

onBeforeMount(async () =>
{
    if (import.meta.env.PROD)
    {
        await checkVersion(`${location.origin}/_nuxt/builds/latest.json`);
    }

    await useThemeStore().initColors();
});

onBeforeUnmount(() =>
{
    $events.$off('errors-modal', toggleErrorsModal);
});

async function checkVersion(url: string)
{
    appHash.value = await loadManifest(url);

    setInterval(async () =>
    {
        const result = await loadManifest(url);

        if (result)
        {
            appHash.value = result;
        }
    },
    frequency.value);
}

async function loadManifest(url: string)
{
    try
    {
        const time = new Date();

        time.setSeconds(0);
        time.setMilliseconds(0);

        const response = await axios.get(`${url}?t=${time.getTime()}`);

        return response.data.id;
    }
    catch (ex)
    {
        handleException($log, ex, {
            0: (ex: any) => $alert.danger(ex.message)
        });

        return null;
    }
}

function toggleErrorsModal(ex: any)
{
    const errors = Object.values(ex ?? {}).flat();

    exceptionErrors.value = [...new Set(errors)] as string[];

    if (errors.length > 0 && !['login', 'forgot-password'].includes((route?.name || '') as string))
    {
        useDialog(errorsDialogId).open();
        $events.$emit('error-occured');
    }
}

function getErrorMessage(error: string)
{
    return error.startsWith('[[[') ? $t(error) : error;
}

watch(appHash, (value, old) =>
{
    if (value !== old && old)
    {
        const message = $t('[[[Dostępna jest nowa aktualizacja. Proszę zapisać postęp pracy i klinąć przycisk "Aktualizuj". Możesz również zaakceptować aktualizację poprzez odświeżenie okna przeglądarki. Brak aktualizacji może spowodować błędy.]]]');
        const duration = 1000 * 3600 * 24;

        $alert.info(message, {
            dismissible: false,
            important: true,
            duration,
            action: {
                label: $t('[[[Aktualizuj]]]'),
                onClick: () => location.reload()
            }
        });
    }
});
</script>

<template>
    <div v-if="!$auth.ready()" class="flex h-full items-center justify-center">
        <Loader />
    </div>
    <NuxtLayout v-else>
        <NuxtPage />
    </NuxtLayout>
    <Toaster
        position="top-center"
        rich-colors
    />
    <IdeoModal :id="errorsDialogId" :title="$t('[[[Wystąpiły błędy podczas zapisywania formularza]]]')" hide-footer>
        <div v-for="(error, index) in exceptionErrors" :key="index" class="flex items-center gap-2 text-red-600 dark:text-red-400" :class="{ 'mb-2': index < exceptionErrors.length - 1 }">
            <IdeoIcon name="triangle-alert" class="shrink-0" />
            <span class="min-w-0">{{ getErrorMessage(error) }}</span>
        </div>

        <div class="text-end">
            <ActionButton
                :text="$t('[[[Zamknij]]]')"
                variant="outline-dark"
                icon="x"
                @click="useDialog(errorsDialogId).close()"
            />
        </div>
    </IdeoModal>
</template>
